import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFormValidationToast } from '../../../services/actionToast';
import { InputForm } from '../../inputs/forms/InputForm';
import { isRequestFailure, useFetch } from '../../../hooks/useFetch';
import { SwitchInputForm } from '../../inputs/forms/SwitchInputForm';
import { NumberInputForm } from '../../inputs/forms/NumberInputForm';
import { FormDialog, FormRow } from '../../ui';

interface TopicFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  defaultValues?: any;
}

const schema = Yup.object().shape({
  name: Yup.string().required('Este campo é obrigatório!'),
  position: Yup.mixed().required('Este campo é obrigatório!'),
  status: Yup.boolean(),
});

const toDefaults = (values: any = {}) => ({
  name: values.name ?? '',
  position: values.position ?? 1,
  status: values.status ?? true,
});

export const TopicFormDialog = ({
  isOpen,
  onClose,
  onSaved,
  defaultValues,
}: TopicFormDialogProps) => {
  const validationToast = useFormValidationToast();
  const baseUrl = '/v1/admin/rating/topics';
  const { requestApi } = useFetch(baseUrl);
  const isEdit = Boolean(defaultValues?.id);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: toDefaults(defaultValues),
  });

  useEffect(() => {
    if (isOpen) reset(toDefaults(defaultValues));
  }, [isOpen, defaultValues, reset]);

  const onSubmit = async (values: any) => {
    const params = { ...values, position: Number(values.position) };
    const result = await requestApi(
      isEdit ? 'put' : 'post',
      params,
      isEdit ? `${baseUrl}/${defaultValues.id}` : null
    );
    if (!isRequestFailure(result)) {
      onSaved();
      onClose();
    }
  };

  return (
    <FormDialog
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Editar tópico' : 'Novo tópico'}
      description="Tópicos organizam o que é avaliado nas jornadas dos alunos."
      onSubmit={handleSubmit(onSubmit, validationToast)}
      isLoading={isSubmitting}
      submitLabel={isEdit ? 'Salvar alterações' : 'Criar tópico'}
    >
      <InputForm
        name="name"
        label="Nome"
        placeholder="Nome do tópico"
        isRequired
        register={register as any}
        errors={errors}
      />
      <FormRow>
        <NumberInputForm
          name="position"
          label="Posição"
          isRequired
          control={control}
          errors={errors}
        />
        <SwitchInputForm name="status" label="Status" errors={errors} control={control} />
      </FormRow>
    </FormDialog>
  );
};
